import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

/**
 * Registers a refetch handler with the MQTT provider so that when the broker
 * reports a non-resumed session (connack.sessionPresent === false), the open
 * chat's queries are invalidated and refetched. This recovers messages that
 * were published while the client was briefly disconnected and the broker had
 * no saved session to replay from.
 *
 * The MqttProvider mounts above QueryClientProvider, so it cannot hold a
 * queryClient itself — a consumer inside the tree registers the callback here.
 *
 * Keep cache/query logic in the controller layer, not in feature components.
 */
export const useMqttChatRefetch = (
  setRefetchHandler: (handler: (() => void) | null) => void,
) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    const handler = () => {
      queryClient.invalidateQueries({ queryKey: ["chat"] });
      queryClient.invalidateQueries({ queryKey: ["chat-window"] });
      queryClient.invalidateQueries({ queryKey: ["thread-messages"] });
      queryClient.invalidateQueries({ queryKey: ["chat-members"] });
    };

    setRefetchHandler(handler);

    return () => {
      setRefetchHandler(null);
    };
  }, [queryClient, setRefetchHandler]);
};
